import type { layout } from "@app/domain";
import type { ChosenLooks, Snapshot, Variant } from "@app/protocol";
import { MATCH_LENGTHS, normalizeRules } from "@app/rules";
import { type ReactNode, useState } from "react";
import { Backdrop } from "../components/Backdrop";
import { CommunityLink } from "../components/CommunityLink";
import { CreateTable, type TableChoice } from "../components/CreateTable";
import { GAMES } from "../components/GameTiles";
import { pickLength, roundGoal } from "../components/gameChoice";
import { HomeNotices } from "../components/HomeNotices";
import { IconButton } from "../components/IconButton";
import { Icon } from "../components/icons";
import { canOpen, startingLooks } from "../components/lookPick";
import { OpenTables, useOpenTables } from "../components/OpenTables";
import { ProfileChip } from "../components/ProfileStrip";
import { Searching } from "../components/Searching";
import { gameSetup, recallTable, type TableSetup } from "../components/tableMemory";
import { useLooks } from "../components/useLooks";
import { useMatch } from "../components/useMatch";
import { useTableMemory } from "../components/useTableMemory";
import { useT } from "../i18n/context";
import { send } from "../ws/singleton";
import { Leaderboard } from "./leaderboard";
import { MyData } from "./myData";

type Tab = "create" | "join";

function TabButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      className={`flex items-center gap-2 rounded-pill px-[18px] py-[9px] text-[14px] font-extrabold ${selected ? "bg-gold text-gold-ink" : "text-ink"}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** "Set up a table | Join a table (N)" (design 2, 2b). */
function Tabs({ tab, onTab, count }: { tab: Tab; onTab: (tab: Tab) => void; count: number }) {
  const { t } = useT();
  const join = tab === "join";
  return (
    <div
      role="tablist"
      className="flex gap-1 self-start rounded-pill border-[1.5px] border-ink/28 bg-panel-strong p-1"
    >
      <TabButton selected={!join} onClick={() => onTab("create")}>
        {t("home.tab.create")}
      </TabButton>
      <TabButton selected={join} onClick={() => onTab("join")}>
        {t("home.tab.join")}
        <span
          className={`rounded-pill px-2 py-0.5 text-[11px] font-extrabold ${join ? "bg-gold-ink/85 text-gold" : "bg-success/20 text-success"}`}
        >
          {count}
        </span>
      </TabButton>
    </div>
  );
}

/**
 * The first screen at an empty table (design 2): set up a table or join an open
 * one. Normal page flow, not the canvas (ADR 0011). See docs/ekranlar/giris.md.
 */
export function Home({ snapshot, measure }: { snapshot: Snapshot; measure: layout.Measure }) {
  const { t } = useT();
  const settings = snapshot.table.settings;
  const [tab, setTab] = useState<Tab>("create");
  // The set-up starts from the last table this player opened (slice 0038) until they change it here.
  const tableMemory = useTableMemory();
  const [edits, setEdits] = useState<Partial<TableSetup>>({});
  const base: TableSetup = {
    ...recallTable(
      {
        variant: settings.variant,
        goal: settings.goal,
        tempo: settings.tempo,
        listed: snapshot.table.listed,
        rules: normalizeRules(settings.variant, settings.rules),
      },
      tableMemory.memory,
    ),
    ...edits,
  };
  const looks = useLooks();
  // The scene and deck start from the player's last choice (decision 118) until they pick here.
  const [picked, setPicked] = useState<Partial<ChosenLooks>>({});
  const { scene, deck } = snapshot.table;
  const choice: TableChoice = { ...base, ...startingLooks({ scene, deck }, looks.chosen, picked) };
  const change = ({ scene, deck, ...rest }: Partial<TableChoice>) => {
    setEdits((c) => ({ ...c, ...rest }));
    setPicked((p) => ({ ...p, ...(scene && { scene }), ...(deck && { deck }) }));
  };
  const tables = useOpenTables();
  const match = useMatch();
  const [board, setBoard] = useState(false);
  const [myData, setMyData] = useState(false);
  const goals = (GAMES.find((g) => g.variant === choice.variant) ?? GAMES[0])?.goals ?? [];
  // King plays 20 or 12 hands and never to a goal; the other games play the table's length.
  const lengths = MATCH_LENGTHS[choice.variant] ?? null;
  const [hands, setHands] = useState<number | null>(null);
  const length = pickLength(lengths, hands, settings.length);
  // Null plays by length (the table's or the URL's choice); a goal not on the list rounds to the top one.
  const goal = roundGoal(choice.goal, goals);
  // A new game brings the target and house rules it was last set up with, else its own.
  const pickGame = (variant: Variant) =>
    setEdits((c) => ({ ...c, variant, ...gameSetup(tableMemory.memory, variant, base.goal) }));
  // Sit first: only the owner may change the settings, and the first seat makes the owner.
  const open = () => {
    match.stop();
    tableMemory.save({ ...base, goal });
    send({ type: "sit", seat: 0 });
    send({ type: "settings", ...choice, goal, length });
  };
  if (board) return <Leaderboard onBack={() => setBoard(false)} />;
  if (myData) return <MyData onBack={() => setMyData(false)} />;
  return (
    <div
      className="relative isolate h-full overflow-y-auto font-ui text-ink"
      data-layout={measure.state}
    >
      <Backdrop />
      <div className="relative mx-auto flex max-w-[1040px] flex-col gap-[22px] px-5 pb-24 pt-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-legible text-[32px] font-extrabold tracking-[-0.01em]">
            {t("home.heading")}
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-green h-[52px] rounded-pill px-6 text-[15px] font-extrabold disabled:cursor-wait disabled:opacity-60"
              disabled={!canOpen(looks.pending) || match.search !== null}
              onClick={() => match.start(choice.variant)}
            >
              {t("home.quick", { game: t(`variant.${choice.variant}`) })}
            </button>
            <IconButton label={t("home.leaderboard")} size={56} onClick={() => setBoard(true)}>
              <Icon name="trophy" />
            </IconButton>
            <ProfileChip variant={choice.variant} onOpen={() => setMyData(true)} />
          </div>
        </header>
        <HomeNotices onMyData={() => setMyData(true)} />
        {match.search !== null && <Searching search={match.search} onStop={match.stop} />}
        <CommunityLink />
        <Tabs tab={tab} onTab={setTab} count={tables.length} />
        {tab === "create" ? (
          <CreateTable
            choice={{ ...choice, goal }}
            goals={goals}
            length={length}
            lengths={lengths}
            onLength={(picked) => {
              setHands(picked);
              change({ goal: null });
            }}
            onChange={change}
            onGame={pickGame}
            onOpen={open}
            looks={looks}
          />
        ) : (
          <OpenTables tables={tables} onCreate={() => setTab("create")} />
        )}
      </div>
    </div>
  );
}
